import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Button } from 'reactstrap';
import Alert from '../../../shared/components/Alert';
import oauthService from '../../../services/oauthService';
import { jsonHeader } from '../../../config';

/**
 * Admin button that registers our developer email with the account's Google Merchant Center.
 * The registration also runs automatically after a Google Ads API onboard; use this to retry
 * or to register accounts that were onboarded before that step existed.
 * Requires the Google refresh token to be stored already.
 */
const GoogleMerchantRegisterButton = ({ accountId }) => {
  const user = useSelector(state => state.authentication.user);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  // null = still checking, true = button needed, false = already registered
  const [needsRegistration, setNeedsRegistration] = useState(null);
  // Data returned by the registration status API when already registered
  const [registration, setRegistration] = useState(null);

  const authOptions = () => ({
    headers: {
      ...jsonHeader,
      authorization: user?.jwtToken ? `JWT ${user.jwtToken}` : undefined,
    },
  });

  useEffect(() => {
    if (!accountId) return undefined;
    let cancelled = false;
    setNeedsRegistration(null);
    setAlert(null);
    oauthService.getGoogleMerchantRegistration({ accountId, options: authOptions() })
      .then((data) => {
        if (cancelled) return;
        // A 200 with data means we are already registered
        setRegistration(data);
        setNeedsRegistration(!data);
      })
      .catch(() => {
        // Non-200 (or failed check): show the button so admins can still register
        if (!cancelled) {
          setRegistration(null);
          setNeedsRegistration(true);
        }
      });
    return () => { cancelled = true; };
  }, [accountId]);

  const handleClick = async () => {
    setLoading(true);
    setAlert(null);
    try {
      await oauthService.registerGoogleMerchantCenter({
        accountId,
        options: authOptions(),
      });
      setAlert({ type: 'SUCCESS', message: 'Merchant Center developer registration completed.' });
      setNeedsRegistration(false);
    } catch (error) {
      setAlert({ type: 'ERROR', message: `Registration failed: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  if (!accountId || needsRegistration === null) {
    return null;
  }

  if (!needsRegistration) {
    return (
      <div className="form__form-group">
        <p><strong>Merchant Center Access:</strong></p>
        <p style={{ marginBottom: '5px' }}>
          <strong style={{ color: '#28a745' }}>REGISTERED</strong>
        </p>
        <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '5px' }}>
          Our developer email is registered with this account&apos;s Google Merchant Center.
        </p>
        {registration && (
          <ul style={{ fontSize: '0.9em', marginLeft: '20px', marginBottom: 0 }}>
            {registration.name && <li>Name: {registration.name}</li>}
            {Array.isArray(registration.gcpIds) && registration.gcpIds.length > 0 && (
              <li>GCP IDs: {registration.gcpIds.join(', ')}</li>
            )}
          </ul>
        )}
        {alert && (
          <div style={{ marginTop: '10px' }}>
            <Alert className={alert.type}>{alert.message}</Alert>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="form__form-group">
      <p><strong>Merchant Center Access:</strong></p>
      <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px' }}>
        Registers our developer email with this account&apos;s Google Merchant Center so the catalog
        APIs can read it. Run after the Google Ads API onboard has stored the refresh token.
      </p>
      <Button
        color="primary"
        outline
        className="account__btn_fit_content"
        disabled={loading}
        onClick={handleClick}
      >
        {loading ? 'Registering...' : 'Register Merchant Center Access'}
      </Button>
      {alert && (
        <div style={{ marginTop: '10px' }}>
          <Alert className={alert.type}>{alert.message}</Alert>
        </div>
      )}
    </div>
  );
};

GoogleMerchantRegisterButton.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

GoogleMerchantRegisterButton.defaultProps = {
  accountId: null,
};

export default GoogleMerchantRegisterButton;
